import { ReactNode, useCallback, useEffect, useState } from 'react';

import {
  Box,
  ButtonBase,
  IconButton,
  Paper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIosOutlinedIcon from '@material-ui/icons/ArrowBackIosOutlined';
import ArrowForwardIosOutlinedIcon from '@material-ui/icons/ArrowForwardIosOutlined';
import CloseIcon from '@material-ui/icons/Close';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import HeadsetIcon from '@material-ui/icons/Headset';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import clsx from 'clsx';
import filesize from 'filesize';
import { useSnackbar } from 'notistack';
import Dropzone from 'react-dropzone';

import { MentionItem } from 'components/ChatInput/mentionSuggestion';
import {
  LOCAL_STORAGE_KEY_INPUT,
  LOCAL_STORAGE_KEY_INPUT_MENTIONS,
} from 'constants/localStorageKeys';
import { FileWithCaption, useFileManager } from 'hooks/useFileManager';
import { useAppDispatch } from 'store';
import { resetReplyMessage } from 'store/reducers/mensagens';

import HiddenFileInput from './HiddenFileInput';
import InputFormCaption from './InputFormCaption';

const useStyles = makeStyles(({ palette, spacing, shape, breakpoints }) => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'hidden',
    height: '100%',
  },
  drag: {
    flexGrow: 1,
    position: 'absolute',
    display: 'flex',
    height: '100%',
    width: '100%',
    zIndex: 10,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: shape.borderRadius,
    backgroundColor: palette.background.paper,
    padding: spacing(2),
    marginBottom: spacing(2),
  },
  rootDragOver: {
    display: 'flex',
    height: '100%',
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    border: `2px dashed ${palette.primary.main}`,
    borderRadius: shape.borderRadius,
    backgroundColor: palette.background.paper,
  },
  container: {
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(2),
    marginBottom: spacing(2),
    borderRadius: shape.borderRadius,
    backgroundColor: palette.background.paper,
    color: palette.text.primary,
  },
  contentPreview: {
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    overflow: 'hidden',
    gap: spacing(2),
    borderRadius: shape.borderRadius,
    backgroundColor: palette.background.default,
  },
  boxPreview: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    justifyContent: 'space-between',
    overflow: 'hidden',
    padding: spacing(4),
    gap: spacing(2),
  },
  image: {
    height: '100%',
    width: '100%',
    borderRadius: spacing(1),
    objectFit: 'contain',
    objectPosition: 'center',
  },
  miniPreviewItem: {
    position: 'relative',
    borderRadius: 8,
    border: `2px solid  ${palette.background.default}`,
    height: spacing(9),
    width: spacing(9),
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
    backgroundColor: palette.background.paper,
    [breakpoints.down('sm')]: {
      height: spacing(4),
      width: spacing(4),
    },
    [breakpoints.down('md')]: {
      height: spacing(6),
      width: spacing(6),
    },
  },
  buttonAddFile: {
    border: `2px solid ${palette.divider}`,
    backgroundColor: palette.background.default,
  },
  miniPreviewItemSelected: {
    border: `2px solid ${palette.primary.main}`,
  },
  audioCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  audio: {
    display: 'flex',
    borderRadius: spacing(1),
    padding: spacing(1, 3),
    background: palette.background.paper,
  },
  iconButton: {
    backgroundColor: palette.background.paper,
    color: palette.text.primary,
    '&:hover': {
      backgroundColor: palette.background.default,
    },
  },
  boxListMiniPreview: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
    [breakpoints.down('md')]: {
      gap: 4,
    },
  },
  boxButtonClose: {
    display: 'flex',
    width: '100%',
    justifyContent: 'end',
    marginTop: 8,
    marginRight: 8,
  },
  boxDocumentPreview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  documentPreviewPaper: {
    padding: spacing(4),
    textAlign: 'center',
    maxWidth: '80%',
  },
}));

type PasteOrDragAndDropFilesProps = {
  onSendFiles?: (
    file: FileWithCaption[],
    callback?: (isSuccess: boolean) => void,
  ) => void;
  initialFiles: File[];
  children?: ReactNode;
  resetInitialFiles?: () => void;
  sessionClosed?: boolean;
  isInstagramOrMessengerChannel?: boolean;
  fetchUsers?: (query: string) => Promise<MentionItem[]>;
  localStorageKeyInput?: string;
  localStorageKeyInputMentions?: string;
};

export default function PasteOrDragAndDropFiles(
  props: PasteOrDragAndDropFilesProps,
) {
  const {
    onSendFiles,
    initialFiles,
    sessionClosed = false,
    isInstagramOrMessengerChannel = false,
    resetInitialFiles,
    children,
    fetchUsers,
    localStorageKeyInput = LOCAL_STORAGE_KEY_INPUT,
    localStorageKeyInputMentions = LOCAL_STORAGE_KEY_INPUT_MENTIONS,
  } = props;

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();

  const {
    addFiles,
    fileUrl,
    files,
    selectedIndex,
    setSelectedIndex,
    handleNextFile,
    handlePrevFile,
    isLimitReached,
    reset: resetFileManager,
    removeFile,
    resetCaption,
    updateCaption,
  } = useFileManager({
    sessionClosed,
    localStorageKeyInput,
    localStorageKeyInputMentions,
  });

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const selectedFile = files[selectedIndex];

  useEffect(() => {
    // Caso o initialValues seja, vazio, significa que o envio já aconteceu
    if (initialFiles.length === 0) {
      resetFileManager();
      return;
    }

    // Caso o initialValues seja, diferente de vazio, significa que o envio ainda nao aconteceu
    if (initialFiles.length > 0) {
      addFiles(initialFiles);
    }
  }, [addFiles, initialFiles, resetFileManager]);

  const handleClearReplyMessage = useCallback(() => {
    dispatch(resetReplyMessage());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      handleClearReplyMessage();
      localStorage.removeItem(localStorageKeyInput);
      localStorage.removeItem(localStorageKeyInputMentions);
    };
  }, [
    resetFileManager,
    handleClearReplyMessage,
    localStorageKeyInput,
    localStorageKeyInputMentions,
  ]);

  const handleClose = useCallback(() => {
    const fileWithCaption = files.find(f => f.caption);

    localStorage.setItem(localStorageKeyInput, fileWithCaption?.caption || '');
    if (fileWithCaption?.contatosMencionados) {
      localStorage.setItem(
        localStorageKeyInputMentions,
        JSON.stringify(fileWithCaption.contatosMencionados),
      );
    } else {
      localStorage.removeItem(localStorageKeyInputMentions);
    }

    localStorage.setItem('restore_caption_flag', 'true');

    resetInitialFiles?.();
    resetFileManager();
  }, [
    resetFileManager,
    resetInitialFiles,
    files,
    localStorageKeyInput,
    localStorageKeyInputMentions,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && files.length > 0) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [files.length, handleClose]);

  const renderPreview = (file: File) => {
    const isImage = file.type.indexOf('image') === 0;
    const isAudio = file.type.indexOf('audio') === 0;

    if (isImage) {
      return <img src={fileUrl(file)} alt="Foto" className={classes.image} />;
    }

    if (isAudio) {
      return (
        <Box className={classes.audioCard}>
          <Box className={classes.audio}>
            <audio controls src={fileUrl(file)}>
              <track kind="captions" />
            </audio>
          </Box>
        </Box>
      );
    }

    return (
      <Box className={classes.boxDocumentPreview}>
        <Paper elevation={0} className={classes.documentPreviewPaper}>
          <InsertDriveFileIcon color="action" style={{ fontSize: '4rem' }} />

          <Typography variant="subtitle1">Prévia indisponível</Typography>

          <Typography color="textSecondary" variant="body2">
            {file.name} -
            {filesize(file.size, {
              round: 0,
              locale: 'pt-BR',
            })}
          </Typography>
        </Paper>
      </Box>
    );
  };

  const renderMiniPreview = (file: File) => {
    const isImage = file.type.indexOf('image') === 0;
    const isAudio = file.type.indexOf('audio') === 0;

    if (isImage) {
      return (
        <img
          src={fileUrl(file)}
          alt="Foto"
          width="100%"
          height="100%"
          style={{ borderRadius: 8, objectFit: 'contain' }}
        />
      );
    }

    if (isAudio) {
      return <HeadsetIcon color="action" fontSize="large" />;
    }

    return <InsertDriveFileIcon color="action" fontSize="large" />;
  };

  const handleDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    if (!isInstagramOrMessengerChannel) {
      addFiles(acceptedFiles);
      return;
    }
    const containsFileNotAllowed = acceptedFiles.some(
      file => !file.type.startsWith('image/'),
    );
    if (containsFileNotAllowed) {
      enqueueSnackbar('Apenas arquivos de imagem são permitidos neste canal.', {
        variant: 'warning',
      });
    }
    const onlyImageFiles = acceptedFiles.filter(file =>
      file.type.startsWith('image/'),
    );
    if (onlyImageFiles.length === 0) return;
    addFiles(onlyImageFiles);
  };

  return sessionClosed ? (
    <Box className={classes.root}>{children}</Box>
  ) : (
    <Dropzone onDrop={handleDrop}>
      {({ getRootProps, isDragActive }) => (
        <Box {...getRootProps()} className={classes.root}>
          {isDragActive && (
            <Box className={classes.drag}>
              <Box className={classes.rootDragOver}>
                <Typography variant="body2">Solte os arquivos aqui</Typography>
              </Box>
            </Box>
          )}

          {files.length > 0 && (
            <Paper className={classes.container} elevation={0}>
              <Box mt={2} className={classes.contentPreview}>
                <Box className={classes.boxButtonClose}>
                  <IconButton onClick={handleClose}>
                    <CloseIcon />
                  </IconButton>
                </Box>

                <Box p={2} className={classes.boxPreview}>
                  {files.length > 1 && (
                    <IconButton
                      onClick={handlePrevFile}
                      className={classes.iconButton}
                    >
                      <ArrowBackIosOutlinedIcon />
                    </IconButton>
                  )}

                  <Box height="100%" width="100%">
                    {selectedFile && <>{renderPreview(selectedFile.file)}</>}
                  </Box>

                  {files.length > 1 && (
                    <IconButton
                      onClick={handleNextFile}
                      className={classes.iconButton}
                    >
                      <ArrowForwardIosOutlinedIcon />
                    </IconButton>
                  )}
                </Box>

                <Box className={classes.boxListMiniPreview}>
                  {files.map((fileOption, index) => (
                    <Box
                      onMouseEnter={event => {
                        event.stopPropagation();

                        setHoveredIndex(index);
                      }}
                      onMouseLeave={event => {
                        event.stopPropagation();
                        setHoveredIndex(null);
                      }}
                      key={`${fileOption.file?.name}-${fileOption.file?.lastModified}`}
                      onClick={() => setSelectedIndex(index)}
                      className={clsx(classes.miniPreviewItem, {
                        [classes.miniPreviewItemSelected]:
                          selectedFile?.file.lastModified ===
                          fileOption.file?.lastModified,
                      })}
                    >
                      {hoveredIndex === index && (
                        <Box
                          style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            zIndex: 2,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'flex-start',
                            width: '100%',
                            height: '100%',
                          }}
                        >
                          <IconButton
                            style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
                            onClick={() => removeFile(index)}
                            size="small"
                          >
                            <CloseRoundedIcon
                              fontSize="small"
                              style={{ color: 'white' }}
                            />
                          </IconButton>
                        </Box>
                      )}

                      {renderMiniPreview(fileOption.file)}
                    </Box>
                  ))}

                  <ButtonBase
                    disabled={isLimitReached}
                    component="label"
                    htmlFor="import-file"
                    className={clsx(
                      classes.miniPreviewItem,
                      classes.buttonAddFile,
                    )}
                  >
                    <AddIcon color="action" style={{ fontSize: '3rem' }} />
                    <HiddenFileInput
                      id="import-file"
                      multiple
                      accept={
                        isInstagramOrMessengerChannel
                          ? 'image/jpeg, image/png'
                          : undefined
                      }
                      onFiles={addFiles}
                    />
                  </ButtonBase>
                </Box>
              </Box>

              <Box mt={2}>
                {selectedFile && (
                  <InputFormCaption
                    disabledInput={selectedFile.file.type.startsWith('audio/')}
                    key={selectedFile.file.lastModified}
                    maxChars={6000}
                    initialValue={files[selectedIndex].caption}
                    initialMentions={files[selectedIndex].contatosMencionados}
                    onInputChange={(caption, contatosMencionados) =>
                      updateCaption(selectedIndex, caption, contatosMencionados)
                    }
                    onSendInputText={(_, callback) => {
                      if (onSendFiles) onSendFiles(files, callback);
                    }}
                    onResetCaption={() => resetCaption(selectedIndex)}
                    fetchUsers={fetchUsers}
                  />
                )}
              </Box>
            </Paper>
          )}

          {files.length === 0 && !isDragActive && children}
        </Box>
      )}
    </Dropzone>
  );
}
